import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import type { LoaderFunctionArgs } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import { apolloClientV4, ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type {
  LatLngInput,
  OutcropOverviewTabQuery,
} from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import { HtmlSection } from '~/components/common/HtmlSection';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import { LastUpdated } from '~/components/common/LastUpdated';
import { NotFound } from '~/components/common/NotFound';
import { Panel } from '~/components/common/Panel';
import { KeyParametersPanelV4 } from '~/components/key-parameters/key-parameters-panel-v4';
import { OutcropMap } from '~/components/outcrop/outcrop-map';
import type { OutcropMapNearbyOutcrop } from '~/components/outcrop/outcrop-map/outcrop-georeference-map';
import { OutcropDetailsPanel } from '~/components/outcrop/OutcropDetailsPanel';
import { OutcropPicturesCarousel } from '~/components/outcrop/OutcropPicturesCarousel';
import { requireRouteParam } from '~/hooks/routing';
import { studyRoute } from '~/paths';
import { rejectNil } from '~/utils/common';
import { fieldLabel } from '~/utils/modules/outcrop';
import { studyGeologyTypes } from '~/utils/modules/study';

const OUTCROP_OVERVIEW_TAB = graphql(`
  query OutcropOverviewTab($id: ID!) {
    outcropGet(id: $id) {
      ...outcropParts
      insertedAt
      updatedAt
      pictures {
        id
        name
        priority
        thumbnailUrl: pictureUrl(width: 300)
      }
      keyParameters {
        ...keyParametersParts
      }
      startAge {
        ...lithostratAgeParts
      }
      endAge {
        ...lithostratAgeParts
      }
      studies {
        id
        name
        collectedYear
        collectedBy
        publicationType
        dominantGeologyType
        keyParameters {
          id
          geologyType
        }
      }
      lithostratOutcropLinks {
        ...lithostratOutcropLinkParts
      }
    }
  }
`);

const OUTCROP_MAP_DATA = graphql(`
  query OutcropMapData($outcropId: ID!) {
    outcropGet(id: $outcropId) {
      id
      center {
        lat
        lng
      }
      georeferences {
        ...georeferenceParts
      }
      crossSections {
        ...crossSectionParts
        georeferences {
          ...georeferenceParts
        }
      }
      sedimentaryLogs {
        ...sedimentaryLogParts
        georeferences {
          ...georeferenceParts
        }
      }
      gigaPans {
        ...gigaPanParts
        georeferences {
          ...georeferenceParts
        }
      }
      virtualOutcropModels {
        ...vomParts
        georeferences {
          ...georeferenceParts
        }
      }
      studies {
        id
        name
        crossSections {
          ...crossSectionParts
          georeferences {
            ...georeferenceParts
          }
        }
        sedimentaryLogs {
          ...sedimentaryLogParts
          georeferences {
            ...georeferenceParts
          }
        }
        gigaPans {
          ...gigaPanParts
          georeferences {
            ...georeferenceParts
          }
        }
      }
    }
  }
`);

const OUTCROP_MAP_NEARBY_OUTCROPS = graphql(`
  query OutcropMap_NearbyOutcrops($location: LatLngInput!) {
    outcropList(
      filter: {
        distanceFromPoint: {
          input: { location: $location }
          lessThanOrEqual: 20000
        }
      }
    ) {
      results {
        id
        name
        distanceFromPoint(location: $location)
        center {
          lat
          lng
        }
      }
    }
  }
`);

async function loadNearbyOutcrops(
  outcropId: number,
  location: LatLngInput | null,
): Promise<OutcropMapNearbyOutcrop[]> {
  if (!location) return [];

  const { data } = await apolloClientV4.query({
    query: OUTCROP_MAP_NEARBY_OUTCROPS,
    variables: {
      location: {
        lat: location.lat,
        lng: location.lng,
      },
    },
  });

  const nearbyOutcrops = data.outcropList?.results
    ?.reduce<OutcropMapNearbyOutcrop[]>((acc, cur) => {
      if (cur.center) {
        acc.push({
          id: parseInt(cur.id),
          name: cur.name,
          center: {
            lat: cur.center.lat,
            lng: cur.center.lng,
          },
          distanceMeters: cur.distanceFromPoint ?? 0,
        });
      }
      return acc;
    }, [])
    // The API returns the outcrop itself in the list
    .filter(oc => oc.id !== outcropId)
    .sort(R.ascend(o => o.distanceMeters));

  return nearbyOutcrops ?? [];
}

async function loader({ params }: LoaderFunctionArgs) {
  const outcropId = requireRouteParam(params, 'outcropId', parseInt);

  const { data } = await apolloClientV4.query({
    query: OUTCROP_OVERVIEW_TAB,
    variables: { id: outcropId },
  });

  const { data: mapData } = await apolloClientV4.query({
    query: OUTCROP_MAP_DATA,
    variables: { outcropId },
  });

  const outcrop = data.outcropGet;
  const outcropMapData = mapData.outcropGet;

  const nearbyOutcrops = await loadNearbyOutcrops(
    outcropId,
    outcropMapData?.center ?? null,
  );

  return {
    outcrop,
    mapData: outcropMapData,
    nearbyOutcrops,
  };
}

export default function OutcropDetailIndexRoute() {
  return (
    <ApolloProviderV4>
      <PageInner />
    </ApolloProviderV4>
  );
}
OutcropDetailIndexRoute.loader = loader;

function PageInner() {
  const { outcrop, mapData, nearbyOutcrops } = useLoaderData<typeof loader>();

  if (!outcrop) return <NotFound />;

  const studies = R.sortWith(
    [R.descend(study => study.collectedYear), R.ascend(study => study.name)],
    outcrop?.studies ?? [],
  );

  const lithostratLinks = R.sortWith(
    [
      R.ascend(link => link.targetType ?? Infinity),
      R.ascend(link => link.targetName ?? Infinity),
    ],
    outcrop.lithostratOutcropLinks,
  );

  const hasPictures = outcrop.pictures.length > 0;
  const hasStudies = studies.length > 0;

  console.log('Map data:', mapData);

  return (
    <>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4">
          <HtmlSection
            title={fieldLabel('introduction', outcrop.outcropCategory)}
            content={outcrop.introduction}
          />
          <HtmlSection
            title={fieldLabel('environments', outcrop.outcropCategory)}
            content={outcrop.environments}
          />
          <HtmlSection
            title={fieldLabel('stratigraphy', outcrop.outcropCategory)}
            content={outcrop.stratigraphy}
          />
          <HtmlSection
            title={fieldLabel(
              'diagenesisAndPetrophysicalProperties',
              outcrop.outcropCategory,
            )}
            content={outcrop.diagenesisAndPetrophysicalProperties}
          />
          <HtmlSection
            title={fieldLabel('structuralGeology', outcrop.outcropCategory)}
            content={outcrop.structuralGeology}
          />
          {/* Production has these two fields reversed from other outcrops: */}
          {outcrop.outcropCategory === 'production' && (
            <>
              {/* Reservoir Engineering Aspects */}
              <HtmlSection
                title={fieldLabel('location', outcrop.outcropCategory)}
                content={outcrop.location}
              />
              {/* Production Aspects */}
              <HtmlSection
                title={fieldLabel(
                  'notablesAndAnalogues',
                  outcrop.outcropCategory,
                )}
                content={outcrop.notablesAndAnalogues}
              />
            </>
          )}

          {outcrop.outcropCategory !== 'production' && (
            <>
              <HtmlSection
                title={fieldLabel(
                  'notablesAndAnalogues',
                  outcrop.outcropCategory,
                )}
                content={outcrop.notablesAndAnalogues}
              />
              <HtmlSection
                title={fieldLabel('location', outcrop.outcropCategory)}
                content={outcrop.location}
              />
            </>
          )}

          <HtmlSection
            title={fieldLabel('safari', outcrop.outcropCategory)}
            content={outcrop.safari} // Key References and Links
          />

          {mapData && (
            <OutcropMap outcrop={mapData} nearbyOutcrops={nearbyOutcrops} />
          )}
        </div>

        <div className="lg:col-span-5 space-y-4">
          {hasPictures && (
            <div>
              <OutcropPicturesCarousel pictures={outcrop.pictures} />
            </div>
          )}

          <div>
            <KeyParametersPanelV4
              keyParameters={outcrop.keyParameters ?? []}
              dominantGeologyType={outcrop.dominantGeologyType}
              outcrop={outcrop}
              leftColMd={4}
              rightColMd={8}
            >
              {(kps, tabs) => (
                <div className="mt-8">
                  <div className="relative bottom-0">
                    <Heading level={3}>Key Parameters</Heading>
                    <div className="absolute bottom-[-2px] right-0">{tabs}</div>
                  </div>

                  <Panel>
                    <Panel.Body>{kps}</Panel.Body>
                  </Panel>
                </div>
              )}
            </KeyParametersPanelV4>
          </div>

          {outcrop.basinName && outcrop.basinType && outcrop.basinClimate && (
            <Panel>
              <Panel.Body>
                <OutcropDetailsPanel
                  outcropId={parseInt(outcrop.id)}
                  basinName={outcrop.basinName}
                  basinType={outcrop.basinType}
                  climate={outcrop.basinClimate}
                  netToGross={outcrop.netToGross}
                  startAge={outcrop.startAge}
                  endAge={outcrop.endAge}
                  lithostratLinks={lithostratLinks}
                  leftColMd={4}
                  rightColMd={8}
                />
              </Panel.Body>
            </Panel>
          )}

          {outcrop.keyWords && (
            <>
              <Heading level={3}>
                {fieldLabel('keyWords', outcrop.outcropCategory)}
              </Heading>
              <Panel>
                <Panel.Body>
                  {outcrop.keyWords.split(',').join(', ')}
                </Panel.Body>
              </Panel>
            </>
          )}

          {hasStudies && (
            <div>
              <Heading level={3}>Studies</Heading>
              <div className="space-y-2">
                {studies.map(study => (
                  <StudyCard key={study.id} study={study} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        {outcrop.author && (
          <p className="text-sm text-muted my-2">
            <i>Author: {outcrop.author}</i>
          </p>
        )}

        <LastUpdated date={outcrop.updatedAt} />
      </div>
    </>
  );
}

type Outcrop = NonNullable<OutcropOverviewTabQuery['outcropGet']>;
type Study = Outcrop['studies'][number];

export function StudyCard({ study }: { study: Study }) {
  const collectionText =
    [study.collectedYear, study.collectedBy, study.publicationType?.join('/')]
      .filter(rejectNil)
      .join(', ') || null;

  const geologyTypes = studyGeologyTypes(study);

  return (
    <div className="card shadow">
      <div className="card-body">
        <Heading level={5}>
          <span className="space-x-1 mr-2">
            {geologyTypes.map(geologyType => (
              <GeologyTypeIconV4
                key={geologyType}
                geologyType={geologyType}
                className="w-6 -mb-1.5"
              />
            ))}
          </span>
          {study.name}
        </Heading>

        {collectionText && <p>{collectionText}</p>}

        <div>
          <Link
            to={studyRoute(parseInt(study.id))}
            className="btn btn-primary btn-sm"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} /> View
          </Link>
        </div>
      </div>
    </div>
  );
}
